import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Input, 
  Modal, 
  Form, 
  Spin, 
  Table,
  Space,
  Popconfirm,
  Empty,
  Typography,
  ConfigProvider,
  Pagination
} from 'antd';
import { 
  FaRegTrashAlt, 
  FaEdit, 
  FaSearch, 
  FaPlus,
  FaChair,
  FaCloudDownloadAlt 
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../api/Api';

const { Title } = Typography;

const GerenciarMesas = () => {
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedMesa, setSelectedMesa] = useState(null);
  const [form] = Form.useForm();

  const primaryColor = 'rgb(249, 130, 68)';
  const secondaryColor = 'rgb(247, 72, 115)';
  const gradient = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;

  // URL base da aplicação
  const URLBase = 'https://socialite.milvendas.ao';

  useEffect(() => {
    fetchData();
    document.title = "Mesas";
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const mesasData = await Api.ListMesa();
      
      setMesas(mesasData.map(m => ({ 
        ...m,
        key: m.id
      })));
    } catch (error) {
      toast.error('Erro ao carregar mesas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gera URL para download do QR Code com moldura Socialite
   * A moldura decorativa é aplicada automaticamente pelo componente QRCodeGenerator
   */
  const gerarQrCode = (descricao, id, token) => {
    // URL que será codificada no QR Code (para onde o cliente será redirecionado)
    const contentUrl = `${URLBase}/vendas/${id}/${token}`;
    
    // Nome do arquivo para download (sem caracteres especiais)
    const safeFilename = `QR_Code_Mesa_${descricao.replace(/[^a-zA-Z0-9]/g, '_')}`;
    
    // URL do componente de download (abre em nova aba)
    // IMPORTANTE: Use /qr-download (com hífen) se for a rota do componente WITH_PREVIEW
    const qrUrl = `${URLBase}/qr-download?content=${encodeURIComponent(contentUrl)}&filename=${encodeURIComponent(safeFilename)}`;
    
    console.log('🎨 Gerando QR Code com moldura Socialite:', {
      mesa: descricao,
      menuUrl: contentUrl,
      downloadUrl: qrUrl
    });
    
    return window.open(qrUrl, "_blank");
  };

  /**
   * Handler de download com tratamento de erro e feedback visual
   */
  const handleDownload = (record) => {
    console.log('📥 Iniciando download do QR Code:', record.descricao);
    
    const newWindow = gerarQrCode(record.descricao, record.id, record.token);
    
    // Verifica se popup foi bloqueado
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      toast.warning('⚠️ Permita popups no navegador para baixar o QR Code', {
        autoClose: 5000
      });
    } else {
      toast.info('🎨 Gerando QR Code com moldura...', {
        autoClose: 2000
      });
    }
  };

  // Filtrar mesas
  const filteredMesas = mesas.filter(mesa => {
    return mesa.descricao?.toLowerCase().includes(searchText.toLowerCase());
  });

  // Paginação
  const paginatedMesas = filteredMesas.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const columns = [
    {
      title: 'Mesa',
      dataIndex: 'descricao',
      key: 'descricao',
      sorter: (a, b) => a.descricao?.localeCompare(b.descricao),
      render: (text) => (
        <Space>
          <FaChair style={{ color: primaryColor }} />
          <span>{text || 'Sem nome'}</span>
        </Space>
      )
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<FaEdit />}
            onClick={() => {
              setSelectedMesa(record);
              form.setFieldsValue({
                descricao: record.descricao
              });
              setIsModalVisible(true);
            }}
            style={{
              color: primaryColor,
              borderColor: primaryColor,
              borderRadius: 6
            }}
            title="Editar mesa"
          />

          <Button 
            icon={<FaCloudDownloadAlt />}
            onClick={() => handleDownload(record)}
            title="Baixar QR Code com Moldura Socialite (300 DPI)"
            style={{
              color: primaryColor,
              borderColor: primaryColor,
              borderRadius: 6
            }}
          />
          
          <Popconfirm
            title="Excluir esta mesa?"
            description="Esta ação não pode ser desfeita."
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
            okButtonProps={{ danger: true }}
          >
            <Button 
              icon={<FaRegTrashAlt />}
              danger
              style={{ borderRadius: 6 }}
              title="Excluir mesa"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      if (selectedMesa) {
        const response = await Api.AlterMesa({ 
          id: selectedMesa.id,
          descricao: values.descricao
        });
        
        if (response.success) {
          fetchData();
          toast.success('Mesa atualizada com sucesso!');
        } else {
          toast.error(response.message || 'Erro ao atualizar mesa');
        }
      } else {
        const response = await Api.AddMesa({ descricao: values.descricao });
        
        if (response.success) {
          fetchData();
          toast.success('Mesa adicionada com sucesso!');
        } else {
          toast.error(response.message || 'Erro ao adicionar mesa');
        }
      }

      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      toast.error('Erro ao salvar mesa: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await Api.DeleteMesa({ id });

      if (response.success) {
        fetchData();
        toast.success('Mesa excluída com sucesso!');
      } else {
        toast.error(response.message || 'Erro ao excluir mesa');
      }
      
      setCurrentPage(1);
    } catch (error) {
      toast.error('Erro ao excluir mesa: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 8,
        },
      }}
    >
      <div style={{ 
        padding: 24,
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        <ToastContainer position="top-right" />
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: 24,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16
        }}>
          <Title level={2} style={{ 
            margin: 0,
            background: gradient,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            MESAS
          </Title>
          
          <Space wrap>
            <Input
              placeholder="Pesquisar mesas..."
              prefix={<FaSearch style={{ color: 'rgba(0,0,0,0.3)' }} />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: 250,
                borderRadius: 8
              }}
              allowClear
            />
            
            <Button 
              type="primary"
              icon={<FaPlus />}
              onClick={() => {
                setSelectedMesa(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(247, 72, 115, 0.3)'
              }}
            >
              Nova Mesa
            </Button>
          </Space>
        </div>

        <Spin spinning={loading}>
          {filteredMesas.length > 0 ? (
            <>
              <Table
                columns={columns}
                dataSource={paginatedMesas}
                pagination={false}
                style={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}
                rowKey="id"
              />
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginTop: 32,
                padding: '16px 0'
              }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={filteredMesas.length}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </div>
            </>
          ) : (
            <Empty
              description={
                <span>
                  {searchText ? 'Nenhuma mesa encontrada com este filtro' : 'Nenhuma mesa cadastrada'}
                </span>
              }
              style={{ 
                marginTop: 50,
                padding: 40,
                background: '#fff',
                borderRadius: 12
              }}
            />
          )}
        </Spin>

        <Modal
          forceRender
          title={
            <span style={{
              background: gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {selectedMesa ? 'Editar Mesa' : 'Nova Mesa'}
            </span>
          }
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsModalVisible(false)}>
              Cancelar
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              loading={loading}
              onClick={() => form.submit()}
              style={{ 
                background: gradient,
                border: 'none',
                borderRadius: 6
              }}
            >
              {selectedMesa ? 'Atualizar' : 'Salvar'}
            </Button>
          ]}
          width={500}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="descricao"
              label="Descrição da Mesa"
              rules={[
                { required: true, message: 'Campo obrigatório' },
                { max: 100, message: 'Máximo 100 caracteres' }
              ]}
            >
              <Input 
                placeholder="Ex: Mesa 01, Terraço VIP, etc."
                maxLength={100}
                showCount
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default GerenciarMesas;